<!--

	An infinitely scrolling vertical list.

	It turned out to be very tricky to realize an actual vertical swiper for variable size slides. In most cases
	it's better to just use a an infinite list if you really need vertical swiping.

-->
<template>
	<conditional :condition="!isEmpty && !hasError" :message-otherwise="hasError ? messageIfError : messageIfEmpty" class="mia-InfiniteLoadingList">
		<transition-group name="mia-InfiniteLoadingList-transition">
			<div v-for="(item, index) of items" :key="keys[index]" class="mia-InfiniteLoadingList-item">
				<list-item-loader :from="item" :item-props="itemProps" :conversions="conversions" :component="options.component" :index="index" />
			</div>
		</transition-group>
		<infinite-loading :margin="`${backendOptions.distance}px`" :identifier="infiniteId" :load="handleInfinite">
			<!-- Override default slots to customize list states -->
			<template #no-results>
				<span />
			</template>
			<template #no-more>
				<span />
			</template>
			<template #loading>
				<div v-if="!isListResetting" class="mia-InfiniteLoadingList-loading">
					<v-progress-circular indeterminate />
				</div>
			</template>
		</infinite-loading>
		<template #replacement>
			<slot name="empty" />
		</template>
	</conditional>
</template>
<script lang="ts">
import { VueEternalLoading } from "@ts-pro/vue-eternal-loading";
import { Component, Watch, toNative } from "vue-facing-decorator";
import I18Next from "../../I18Next";
import Conditional from "./../../ConditionalReplacement.vue";
import ListItemLoader from "./ListItemLoader.vue";
import { VueListBase } from "./VueListBase";
import { computedAsync } from "@vueuse/core";
import { type IInfiniteLoadingOptions } from "./VueListOptions";
import { VProgressCircular } from "vuetify/components";

@Component({
	components: {
		Conditional,
		InfiniteLoading: VueEternalLoading,
		ListItemLoader,
		VProgressCircular,
	},
})
export class InfiniteLoadingList<S extends string | number, T> extends VueListBase<S, T, IInfiniteLoadingOptions> {
	// When something goes wrong in this list, often the infinite loader loads items foverer,
	// this tends to leave the browser tab unrespansive, so it's helpful (mostly for development)
	// to intercept errors and display a message instead.
	hasError = false;

	infiniteId = +new Date();

	messageIfError = computedAsync(() => I18Next.tr("ListFailedToLoad_GenericError"), "…");

	protected errorCaptured(): void { this.hasError = true; }

	get isListResetting(): number { return this.isResetting; }

	@Watch("isResetting")
	protected onReset(isResetting: number): void {
		if (!isResetting) {
			this.goto(0);
			// Restart the infinite loading if we have reached the end of the list in the meantime
			this.infiniteId++;
		}
	}

	handleInfinite($callback: { noMore: () => void, loaded: () => void }) {
		const lengthBefore = this.items.length;
		void this.preload(this.items.length).then(() => {
			const lengthAfter = this.items.length;
			if (lengthBefore === lengthAfter) {
				$callback.noMore();
			}
			else {
				$callback.loaded();
			}
		});
	}
}

export default toNative(InfiniteLoadingList);
</script>
<style lang="scss">
.mia-InfiniteLoadingList {
	.mia-InfiniteLoadingList-loading {
		display: flex;
		justify-content: center;
		padding: theme.$mia-padding-panel-large;

		.v-progress-circular {
			color: theme.$mia-color-primary-500;
		}
	}
}

// Default transition for the "InfiniteLoadingList" component.
//
// - Uses the default class prefix of `mia-InfiniteLoadingList-transition`.
// - These styles are applied by Vue's <TransitionGroup> component.
//
// NOTE: This is a simple zoom-in-out animation to add/remove elements from lists.
// Not much thought has been put into this so feel free to modify it.
.mia-InfiniteLoadingList-transition-move,
.mia-InfiniteLoadingList-transition-enter-active,
.mia-InfiniteLoadingList-transition-leave-active {
	transition: all 0.5s ease;
}

.mia-InfiniteLoadingList-transition-enter-from,
.mia-InfiniteLoadingList-transition-leave-to {
	opacity: 0;
}

.mia-InfiniteLoadingList-transition-leave-active {
	animation: mia-animation-zoomOutDown .5s both;
}

@keyframes mia-animation-zoomOutDown {
	40% {
		transform: scale3d(.475, .475, .475) translate3d(0, -60px, 0);
		animation-timing-function: cubic-bezier(0.550, 0.055, 0.675, 0.190);
	}
	100% {
		transform: scale3d(.1, .1, .1) translate3d(0, 2000px, 0);
		transform-origin: center bottom;
		animation-timing-function: cubic-bezier(0.175, 0.885, 0.320, 1);
	}
}
</style>
